import React, { useState } from "react";
import { toast } from "sonner";
import { BellRing, FileCheck2, FileWarning, FileX2, Loader2, Lock, Unlock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATE = {
  complete: { label: "Documents complets", cls: "bg-[#D6F5D6] text-emerald-800", Icon: FileCheck2 },
  partial: { label: "Documents incomplets", cls: "bg-[#FFF3CC] text-amber-800", Icon: FileWarning },
  none: { label: "Aucun document", cls: "bg-[#FFEAE5] text-red-700", Icon: FileX2 },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null);

// Bandeau documents sous un covoitureur (onglet Covoitureurs rapide).
export default function DriverDocsStrip({ d, onChanged }) {
  const [busy, setBusy] = useState(null);
  const docs = d.docs || {};
  const st = STATE[docs.state] || STATE.none;
  const blocked = !!d.driver_missing_docs_block?.active;
  const uid = d.user_id;
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onChanged?.(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  return (
    <div className="mt-1.5 rounded-2xl border-2 border-black bg-white px-3 pb-3 pt-2" data-testid={`driver-docs-${uid}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin ${st.cls}`} data-testid={`driver-docs-state-${uid}`}>
          <st.Icon className="h-3 w-3" />{st.label} · {docs.sent ?? 0}/{docs.required ?? 6}
        </span>
        {docs.validated_without_docs && (
          <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white" data-testid={`driver-docs-validated-without-${uid}`}>Validé sans documents</span>
        )}
        {blocked && <span className="rounded-full bg-black px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white" data-testid={`driver-docs-blocked-${uid}`}>Rôle conducteur suspendu</span>}
      </div>
      {docs.state !== "complete" && (
        <>
          <p className="mt-1.5 text-[11px] font-semibold text-foreground/70" data-testid={`driver-docs-missing-${uid}`}>Manquant : {(docs.missing_labels || []).join(", ")}</p>
          {d.missing_docs_reminded_at && (
            <p className="text-[10px] font-semibold text-foreground/50" data-testid={`driver-docs-reminded-${uid}`}>Relancé {d.missing_docs_reminders || 1} fois · dernière relance {fmt(d.missing_docs_reminded_at)}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button type="button" disabled={!!busy} onClick={() => run("remind", () => api.post(`/admin/price/docs/${uid}/remind-missing`), "Relance envoyée (app, push et e-mail)")}
              className="street-press inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary-foreground street-border-thin disabled:opacity-60" data-testid={`driver-docs-remind-${uid}`}>
              {busy === "remind" ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />}Relancer
            </button>
            {!blocked ? (
              <button type="button" disabled={!!busy} data-testid={`driver-docs-block-${uid}`}
                onClick={() => window.confirm("Suspendre le rôle conducteur jusqu'à l'envoi et la validation des documents ? Le compte passager reste actif.")
                  && run("block", () => api.post(`/admin/price/docs/${uid}/missing-block`, { active: true }), "Rôle conducteur suspendu")}
                className="street-press inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-red-600 street-border-thin disabled:opacity-60">
                {busy === "block" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}Suspendre conducteur
              </button>
            ) : null}
          </div>
        </>
      )}
      {blocked && (
        <button type="button" disabled={!!busy} onClick={() => run("unblock", () => api.post(`/admin/price/docs/${uid}/missing-block`, { active: false }), "Rôle conducteur rétabli")}
          className="street-press mt-2 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700 street-border-thin disabled:opacity-60" data-testid={`driver-docs-unblock-${uid}`}>
          {busy === "unblock" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlock className="h-3 w-3" />}Réactiver conducteur
        </button>
      )}
    </div>
  );
}

const FILTERS = [["", "Tous"], ["incomplete", "Incomplets"], ["none", "Aucun doc"], ["approved_missing", "Validés sans docs"]];

// Filtres documents + relance groupée.
export function DriverDocsFilters({ value, onChange, counts, onReminded, q, statusFilter }) {
  const [busy, setBusy] = useState(false);
  const remindAll = async () => {
    const n = counts?.[value] ?? 0;
    if (!window.confirm(`Relancer ${n} covoitureur(s) par notification, push et e-mail ?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/price/docs/remind-missing-bulk", { docs_filter: value, q: q || null, status_filter: statusFilter || null }); toast.success(`${data.sent} relance(s) en cours d'envoi`); setTimeout(() => onReminded?.(), 2500); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid="drivers-fast-docs-filters">
      <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-foreground/60">Documents</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map(([v, l]) => (
          <button key={v || "all"} type="button" onClick={() => onChange(v)} data-testid={`drivers-fast-docs-${v || "all"}`}
            className={`flex-none rounded-lg border-2 border-black px-3 py-1.5 text-[11px] font-black uppercase tracking-wider ${value === v ? "bg-primary text-primary-foreground" : "bg-white text-foreground"}`}>
            {l}{v && counts ? ` (${counts[v] ?? 0})` : ""}
          </button>
        ))}
      </div>
      {value && (counts?.[value] ?? 0) > 0 && (
        <button type="button" disabled={busy} onClick={remindAll} data-testid="drivers-fast-docs-remind-all"
          className="street-press mt-2 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-primary-foreground street-border-thin disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />}Relancer tous ({counts[value]})
        </button>
      )}
    </div>
  );
}
